import { LogLevel, type Configuration } from '@azure/msal-browser';
import type { MsalEnvironmentConfig } from '@/types/auth.types';

export function getMsalEnvironmentConfig(): MsalEnvironmentConfig {
  const clientId = import.meta.env.VITE_MSAL_CLIENT_ID as string | undefined;
  const authority = import.meta.env.VITE_MSAL_AUTHORITY as string | undefined;

  if (!clientId) {
    throw new Error(
      'MSAL configuration error: VITE_MSAL_CLIENT_ID environment variable is required'
    );
  }

  if (!authority) {
    throw new Error(
      'MSAL configuration error: VITE_MSAL_AUTHORITY environment variable is required'
    );
  }

  // NOTE: use `||` rather than `??` so that an empty string in the .env file
  // (e.g. `VITE_MSAL_REDIRECT_URI=`) also falls back to `window.location.origin`.
  // Otherwise MSAL receives "" and throws `Failed to construct 'URL': Invalid URL`.
  const redirectUri =
    (import.meta.env.VITE_MSAL_REDIRECT_URI as string | undefined) || window.location.origin;
  const postLogoutRedirectUri =
    (import.meta.env.VITE_MSAL_POST_LOGOUT_URI as string | undefined) || window.location.origin;

  return {
    clientId,
    authority,
    redirectUri,
    postLogoutRedirectUri,
  };
}

export function createMsalConfig(): Configuration {
  const envConfig = getMsalEnvironmentConfig();
  const isDebugMode = import.meta.env.VITE_DEBUG_MODE === 'true';

  return {
    auth: {
      clientId: envConfig.clientId,
      authority: envConfig.authority,
      redirectUri: envConfig.redirectUri,
      postLogoutRedirectUri: envConfig.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii) {
            return;
          }

          if (isDebugMode || level <= LogLevel.Warning) {
            /* eslint-disable no-console */
            const logMethod =
              level === LogLevel.Error
                ? console.error
                : level === LogLevel.Warning
                  ? console.warn
                  : console.debug;
            logMethod(`[MSAL] ${message}`);
            /* eslint-enable no-console */
          }
        },
        piiLoggingEnabled: false,
        logLevel: isDebugMode ? LogLevel.Info : LogLevel.Warning,
      },
    },
  };
}

export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

export const tokenRequest = {
  scopes: ['User.Read'],
};
